/**
 * VehicleSubscriptionsTab - Sub-aba "Recorrência" da aba Financeiro do veículo
 *
 * Espelha `CustomerSubscriptionsTab`, mas filtra por `vehicle_id` na RPC
 * `get_assinaturas_paginated`. A coluna de cliente permanece visível para
 * identificar o proprietário do documento na época da emissão.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-19 v1.0.0 - Criação (refatoração da aba Financeiro do veículo)
 */
import { useState } from 'react';
import {
  useFinanceiroAssinaturasPaginated,
  PaginatedAssinatura,
} from '@/hooks/useFinanceiroAssinaturasPaginated';
import { SubscriptionsTable } from '@/components/financial/SubscriptionsTable';
import { SubscriptionDetailsModal } from '@/components/financial/SubscriptionDetailsModal';
import { PaginationFooter } from '@/components/ui/pagination-footer';

interface VehicleSubscriptionsTabProps {
  /** ID do veículo */
  vehicleId: string;
  /** Lazy loading: só busca quando a sub-aba estiver ativa */
  isActive?: boolean;
  /** Permissão para pausar/reativar recorrência (financial_charges.can_edit) */
  canEdit?: boolean;
  /** Permissão para cancelar recorrência (financial_charges.can_delete) */
  canDelete?: boolean;
}

export function VehicleSubscriptionsTab({
  vehicleId,
  isActive = false,
  canEdit,
  canDelete,
}: VehicleSubscriptionsTabProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const { assinaturas, totalCount, isLoading } = useFinanceiroAssinaturasPaginated(
    isActive ? { vehicle_id: vehicleId } : undefined,
    { page: currentPage, pageSize },
  );

  const handleRowClick = (a: PaginatedAssinatura) => {
    setSelectedId(a.id);
    setDetailsOpen(true);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Tabela rolável (coluna de cliente visível = proprietário do documento) */}
      <div className="flex-1 overflow-auto">
        <SubscriptionsTable
          assinaturas={assinaturas}
          isLoading={isLoading}
          onRowClick={handleRowClick}
          canEdit={canEdit}
          canDelete={canDelete}
          emptyMessage="Nenhuma recorrência encontrada para este veículo"
        />
      </div>

      {/* Paginação */}
      {!isLoading && totalCount > 0 && (
        <div className="border-t px-4 py-2 bg-background">
          <PaginationFooter
            totalItems={totalCount}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={size => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            pageSizeOptions={[10, 20, 50, 100]}
            borderClassName=""
            className="mt-0 pt-0"
          />
        </div>
      )}

      {/* Modal de detalhes da recorrência */}
      <SubscriptionDetailsModal
        assinaturaId={selectedId}
        open={detailsOpen}
        onOpenChange={v => {
          setDetailsOpen(v);
          if (!v) setSelectedId(null);
        }}
      />
    </div>
  );
}
